import { useMutation, useQuery, useQueryClient, UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import { useCallback } from 'react';

import { deleteDocument, downloadDocument, searchDocuments } from '../api/documents';
import { Document } from '../interfaces/documents';

import { fetchDocumentFile } from 'modules/Documents/api/documents';
import openDocument from 'modules/Documents/openDocument';

function useDocuments<T = Document[]>(
    selectFunction: (data: Document[]) => T,
    options: Omit<UseQueryOptions<Document[], unknown, T, [string, { [key: string]: any }]>, 'queryKey'>,
    data: { [key: string]: any } = {}
): UseQueryResult<T, unknown> {
    const queryKey: [string, { [key: string]: any }] = ['documents', data];

    return useQuery<Document[], unknown, T, [string, { [key: string]: any }]>({
        queryKey,
        queryFn: () => searchDocuments(data),
        select: useCallback(selectFunction, []),
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        ...options,
    });
}

export const useDocumentFiltered = (
    filters: { [key: string]: any },
    options: Omit<UseQueryOptions<Document[], unknown, Document[], [string, { [key: string]: any }]>, 'queryKey'>,
    data = {}
) =>
    useDocuments<Document[]>(
        (documents) =>
            documents.filter((document) =>
                Object.keys(filters).every((key) => key in document && document[key as keyof Document] === filters[key])
            ),
        options,
        data
    );

export const useDocumentsListByType = (
    options: Omit<
        UseQueryOptions<Document[], unknown, Record<string, Document[]>, [string, { [key: string]: any }]>,
        'queryKey'
    > = {},
    data = {}
) =>
    useDocuments<Record<string, Document[]>>(
        (documents) =>
            documents.reduce(
                (acc, cur) => ({
                    ...acc,
                    [cur.document_type]: [...(acc[cur.document_type] ?? []), cur],
                }),
                {} as Record<string, Document[]>
            ),
        options,
        data
    );

export const usePreviewDocumentMutation = (isAllowed = false) => {
    return useMutation({
        mutationFn: async (document: Document) => fetchDocumentFile(isAllowed, document),
        onSuccess: ({ data, document: { file_name } }) => {
            openDocument(data, file_name);
        },
    });
};

export const useDownloadMutation = (id: any) => {
    return useMutation({
        mutationFn: () => downloadDocument(id),
    });
};

export const useDocumenteDeleteMutation = (id: any, isEmployee = true) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: () => deleteDocument(id, isEmployee),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['documents'],
            });
        },
    });
};
